import { getStrWithCommas } from "../../utils/string";
import { formatPrice } from "./pricing-utils";

const ACADEMIC_PRICE_MODIFIER = 0.5;
const RENEWAL_PRICE_MODIFIER = 0.5;

const getUnitRangeText = (unitLimitLower, unitLimitUpper, unlimitedLabel) =>
  `${getStrWithCommas(unitLimitLower)} ${
    unitLimitUpper ? `- ${getStrWithCommas(unitLimitUpper)}` : unlimitedLabel
  }`;

const getPricingText = (currencyCode, price, pricingPerUnitLabel) =>
  `${currencyCode} ${getStrWithCommas(
    formatPrice(price, currencyCode)
  )} ${pricingPerUnitLabel}`;

const getVerticalTierTR = (
  tr,
  currencyCode,
  firstCellReplacement,
  { unitLimitLower, unitLimitUpper, price },
  pricingPerUnitLabel,
  unlimitedLabel,
  pricingModifier,
  includeAcademic,
  academicDiscountOnFirstTier,
  tierIndex
) => {
  const {
    children: [unitTD, priceTD, academicTD],
  } = tr;
  const academicPriceAdjustment =
    (includeAcademic && tierIndex > 0) ||
    (includeAcademic && academicDiscountOnFirstTier)
      ? pricingModifier * ACADEMIC_PRICE_MODIFIER
      : pricingModifier;

  unitTD.innerText =
    tierIndex === 0 && firstCellReplacement && firstCellReplacement.length > 0
      ? firstCellReplacement
      : getUnitRangeText(unitLimitLower, unitLimitUpper, unlimitedLabel);
  priceTD.innerText = getPricingText(
    currencyCode,
    adjustPricing(price, pricingModifier),
    pricingPerUnitLabel
  );
  if (includeAcademic) {
    academicTD.innerText = getPricingText(
      currencyCode,
      adjustPricing(price, academicPriceAdjustment),
      pricingPerUnitLabel
    );
  }

  return tr;
};

const adjustPricing = (price, pricingModifier) => price * pricingModifier;

const getTemplRow = (tagStr, numTiers, isHorizontal, includeAcademic) =>
  tagStr.repeat(isHorizontal ? numTiers + 1 : includeAcademic ? 3 : 2);

const onPricing = async (
  domRoot,
  {
    pricing,
    firstCellReplacement,
    flatRateLabel,
    perUnitLabel,
    marginalPerUnitLabel,
    heading1,
    heading2,
    academicHeading,
    isHorizontal,
    productName,
    productURL,
    unlimitedLabel,
    isRenewal,
    includeAcademic,
    academicDiscountOnFirstTier,
    startingRowIndex,
    stoppingRowIndex,
    appendedLastRowText,
  }
) => {
  const { pricingTiers, currencyCode } = await pricing;

  const pricingStrategyToLabel = {
    PER_UNIT: perUnitLabel,
    MARGINAL_PER_UNIT: marginalPerUnitLabel,
    FIXED_COST: flatRateLabel,
  };

  const templTHRow = document.createElement("tr");
  templTHRow.innerHTML = getTemplRow(
    "<th></th>",
    pricingTiers.length,
    isHorizontal,
    includeAcademic
  );

  const templRow = document.createElement("tr");
  templRow.innerHTML = getTemplRow(
    "<td></td>",
    pricingTiers.length,
    isHorizontal,
    includeAcademic
  );

  const theaderNode = domRoot.querySelector("thead");
  const tbodyNode = domRoot.querySelector("tbody");

  if (!isHorizontal) {
    const productNameRow = document.createElement("tr");
    const colspan = includeAcademic ? 3 : 2;
    productNameRow.innerHTML = `<th colspan="${colspan}" class="no-header"><a href="${productURL}">${productName}</a></th>`;
    theaderNode.appendChild(productNameRow);

    const [heading1TH, heading2TH, heading3TH] = templTHRow.children;
    heading1TH.innerHTML = heading1;
    heading2TH.innerHTML = heading2;
    if (includeAcademic) {
      heading3TH.innerHTML = academicHeading;
    }
    theaderNode.appendChild(templTHRow);

    for (const [tierIndex, tier] of pricingTiers.entries()) {
      if (startingRowIndex && tierIndex < startingRowIndex) {
        continue;
      }

      let pricingModifier =
        isRenewal && tierIndex > 0 ? RENEWAL_PRICE_MODIFIER : 1;

      tbodyNode.appendChild(
        getVerticalTierTR(
          templRow.cloneNode(true),
          currencyCode,
          firstCellReplacement,
          tier,
          pricingStrategyToLabel[tier.pricingStrategy],
          unlimitedLabel,
          pricingModifier,
          includeAcademic,
          academicDiscountOnFirstTier,
          tierIndex
        )
      );

      if (stoppingRowIndex && tierIndex >= stoppingRowIndex) {
        break;
      }
    }

    if (appendedLastRowText) {
      const appendedLastRow = document.createElement("tr");
      appendedLastRow.innerHTML = `<td colspan="${colspan}">${appendedLastRowText}</td>`;
      tbodyNode.appendChild(appendedLastRow);
    }
  } else {
    templTHRow.children[0].innerHTML = heading1;
    for (let i = 1; i < templTHRow.children.length; i++) {
      const { unitLimitLower, unitLimitUpper } = pricingTiers[i - 1];

      templTHRow.children[i].innerHTML = getUnitRangeText(
        unitLimitLower,
        unitLimitUpper,
        unlimitedLabel
      );
    }
    theaderNode.appendChild(templTHRow);

    templRow.children[0].innerHTML = `<a href="${productURL}">${productName}</a>`;
    for (let j = 1; j < templRow.children.length; j++) {
      const { price, pricingStrategy } = pricingTiers[j - 1];

      templRow.children[j].innerHTML = getPricingText(
        currencyCode,
        price,
        pricingStrategyToLabel[pricingStrategy]
      );
    }
    tbodyNode.appendChild(templRow);
  }

  domRoot.classList.remove("loading");
};

export default class LicensingTableLocalized {
  constructor(domRoot: HTMLElement, params: any) {
    onPricing(domRoot, params);
  }
}
